/**
 * LoadingBar — 영역 맨 위 2px 진행 막대 (DESIGN-AI §30 · 패턴 D·H)
 * ----------------------------------------------------------------------------
 * 내용이 이미 있는 상태에서 다시 받을 때(새로고침·필터 바꾸기) 내용을 비우지 않고 이것만 지나가게 한다.
 * 부모는 position: relative 여야 한다(막대는 absolute top 0).
 * delay(기본 300ms) 안에 끝나면 아예 안 보인다 — 짧은 요청마다 깜빡이지 않게.
 *
 *   <div className="relative"><LoadingBar active={refreshing} /> … 표 … </div>
 *
 * ★ prefers-reduced-motion 이면 움직이지 않는 흐린 막대. 키프레임은 처음 쓸 때 <head> 에 한 번(Skeleton 과 같은 방식).
 */
import React from "react";

const STYLE_ID = "vault-loading-bar-style";
const CSS = `
@keyframes vault-loading-bar { from { left: -35%; } to { left: 100%; } }
.vault-loading-bar { position: absolute; left: 0; right: 0; top: 0; height: 2px; overflow: hidden; z-index: 5; pointer-events: none; }
.vault-loading-bar::after { content: ""; position: absolute; top: 0; bottom: 0; width: 35%; background: var(--accent, #005bf6); animation: vault-loading-bar 1.1s cubic-bezier(.2,.8,.2,1) infinite; }
@media (prefers-reduced-motion: reduce) { .vault-loading-bar::after { animation: none; left: 0; width: 100%; opacity: .5; } }
`;

export function useLoadingBarStyle() {
  React.useEffect(() => {
    if (typeof document === "undefined" || document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }, []);
}

/** active 가 delay 이상 이어질 때만 true */
export function useDelayedFlag(active: boolean, delay = 300): boolean {
  const [shown, setShown] = React.useState(false);
  React.useEffect(() => {
    if (!active) { setShown(false); return undefined; }
    if (delay <= 0) { setShown(true); return undefined; }
    const id = window.setTimeout(() => setShown(true), delay);
    return () => window.clearTimeout(id);
  }, [active, delay]);
  return shown;
}

export default function LoadingBar({ active, delay = 300, label = "불러오는 중" }: { active: boolean; delay?: number; label?: string }) {
  useLoadingBarStyle();
  const shown = useDelayedFlag(active, delay);
  if (!shown) return null;
  return <div className="vault-loading-bar" role="progressbar" aria-label={label} aria-busy="true" />;
}
